<!-- Loading indicator -->
<div *ngIf="isLoading" class="loading-container">
  <loading message="Loading household assignments..."></loading>
</div>

<!-- Content -->
<div *ngIf="!isLoading">
  <!-- Header -->
  <div class="section-header">
    <div class="header-content">
      <div class="header-text">
        <h3>Assigned Households</h3>
        <p>A list of households currently assigned to {{ user.FIRST_NAME }} {{ user.LAST_NAME }}</p>
      </div>
      <div class="header-actions" *ngIf="canManageAssignments">
        <button *ngIf="!showHouseholdSearch" class="text-btn" (click)="toggleHouseholdSearch()">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span>Assign Household</span>
        </button>

        <div *ngIf="showHouseholdSearch" class="assign-household-form">
          <div class="form-group search search-wrapper">
            <input
              type="text"
              placeholder="Type a household name to search..."
              [(ngModel)]="householdSearchQuery"
              (ngModelChange)="onHouseholdSearch($event)"
              (focus)="onHouseholdSearchFocus()"
              (blur)="onHouseholdSearchBlur()"
            />
            <div *ngIf="isHouseholdSearchFocused && (allHouseholds.length > 0 || noHouseholdResults || isSearchingHouseholds)" class="search-results">
              <div *ngIf="isSearchingHouseholds" class="search-result-item searching">
                <loading message="Searching..."></loading>
              </div>
              <div *ngFor="let household of allHouseholds" class="search-result-item" (click)="selectHousehold(household)">
                {{ household.HOUSEHOLD_NAME }}
              </div>
              <div *ngIf="noHouseholdResults" class="search-result-item no-results">
                No results found
              </div>
              <div *ngIf="allHouseholds.length > 0" class="search-results-footer">
                Showing {{ allHouseholds.length }} results for "{{ householdSearchQuery }}"
              </div>
            </div>
          </div>
          <div class="button-group" style="display: flex; align-items: center; gap: 10px;">
            <div (click)="assignHousehold()">
              <saffron-button
                text="Assign"
                data-testid="btn-assign-household"
                type="primary"
                size="small"
                [width]="'80px'"
                [height]="'28px'"
                [is_loading]="isAssigningHousehold"
                [is_disabled]="!selectedHouseholdId || isAssigningHousehold"
              ></saffron-button>
            </div>
            <button class="btn btn-secondary btn-small" (click)="toggleHouseholdSearch()">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Empty state -->
  <div *ngIf="assignments.length === 0" class="empty-state">
    <div class="empty-icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9,22 9,12 15,12 15,22"></polyline>
      </svg>
    </div>
    <p class="empty-message">{{ isViewingAdvisor() ? 'No households assigned to this advisor' : 'No households assigned to this manager' }}</p>
  </div>

  <!-- Households list -->
  <div *ngIf="assignments.length > 0" class="list-container">
    <div *ngFor="let assignment of assignments" class="list-item">
      <div class="item-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      </div>
      <div class="item-details" (click)="navigateToHousehold(assignment.household.HOUSEHOLD_ID)">
        <span class="item-name">{{ assignment.household.HOUSEHOLD_NAME }}</span>
        <span class="item-subtitle">ID: {{ assignment.household.HOUSEHOLD_ID }}</span>
        <div class="item-meta">
          <span>Assigned {{ formatDate(assignment.CREATED_AT) }} by {{ getDisplayName(assignment.assignedBy) }}</span>
        </div>
      </div>
      <div class="household-actions">
        <button class="btn btn-secondary btn-small" (click)="removeHouseholdAssignment(assignment)" title="Remove assignment">Remove</button>
      </div>
    </div>

    <!-- Pagination footer -->
    <div class="pagination-footer flex-row flex-center-y flex-space-between">
      <div class="pagination-info">
        <p>
          Showing {{ showingFrom }}–{{ showingTo }} of {{ totalItems }} households
        </p>
      </div>
      <div class="pagination-controls flex-row flex-center-y">
        <button class="btn btn-secondary btn-small" 
                (click)="firstPage()" 
                [disabled]="currentPage === 1">
                  « First
        </button>
        <button class="btn btn-secondary btn-small" 
                (click)="prevPage()" 
                [disabled]="currentPage === 1" 
                style="margin-left: 6px;">
                  ‹ Prev
        </button>
        <span style="margin: 0 10px;">Page {{ currentPage }} of {{ totalPages }}</span>
        <button class="btn btn-secondary btn-small" 
                (click)="nextPage()" 
                [disabled]="currentPage === totalPages || totalPages === 0" 
                style="margin-right: 6px;">
                  Next ›
        </button>
        <button class="btn btn-secondary btn-small" 
                (click)="lastPage()" 
                [disabled]="currentPage === totalPages || totalPages === 0">
                  Last »
        </button>
        <div style="margin-left: 16px;" class="page-size">
          <label for="pageSize" style="margin-right: 6px;">Per page</label>
          <select id="pageSize" [ngModel]="pageSize" (ngModelChange)="onPageSizeChange($event)">
            <option [ngValue]="10">10</option>
            <option [ngValue]="25">25</option>
            <option [ngValue]="50">50</option>
            <option [ngValue]="100">100</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</div>
